import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Button,
  Callout,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  toast,
} from '@opengoal/ui';
import { useTranslation } from 'react-i18next';
import { toDateString } from '@/shared/lib/formSchema';
import type { OffboardingDetailDto, RecordTerminationDto } from '@/api/generated/model';
import { RecordTerminationDtoReason } from '@/api/generated/model';
import { PlayerContractsPanel } from '@/modules/players';
import { useRecordTerminationMutation } from '../hooks/useOffboarding';
import { StepSection } from './StepSection';

const REASONS = Object.values(RecordTerminationDtoReason);

export interface TerminationPanelProps {
  offboarding: OffboardingDetailDto;
  readOnly?: boolean;
}

/**
 * Step 1 — the contract termination record.
 *
 * Every other deadline on this screen is dated from what this form captures,
 * which is why its step is due on day zero and why the three below it stay
 * locked until it is marked done.
 */
export function TerminationPanel({ offboarding, readOnly = false }: TerminationPanelProps) {
  const { t } = useTranslation();
  const record = useRecordTerminationMutation(offboarding.id);

  const schema = z.object({
    reason: z.string().min(1, t('offboarding.termination.reasonRequired')),
    noticeServedAt: z.string(),
    effectiveDate: z.string().min(1, t('offboarding.termination.effectiveDateRequired')),
    notes: z.string().max(4000),
  });
  type TerminationFormValues = z.infer<typeof schema>;

  const seed = (): TerminationFormValues => ({
    reason: offboarding.reason ?? '',
    noticeServedAt: offboarding.noticeServedAt?.slice(0, 10) ?? '',
    effectiveDate: offboarding.effectiveDate?.slice(0, 10) ?? '',
    notes: offboarding.notes ?? '',
  });

  const form = useForm<TerminationFormValues>({
    resolver: zodResolver(schema),
    defaultValues: seed(),
  });

  useEffect(() => {
    form.reset(seed());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offboarding.id]);

  const submit = (values: TerminationFormValues) => {
    const dto: RecordTerminationDto = {
      reason: values.reason as RecordTerminationDtoReason,
      noticeServedAt: toDateString(values.noticeServedAt) ?? null,
      effectiveDate: toDateString(values.effectiveDate) ?? null,
      notes: values.notes.trim() || null,
    };

    record.mutate(dto, {
      onSuccess: () => toast.success(t('offboarding.termination.saved')),
      onError: (error: unknown) =>
        toast.error(
          error instanceof Error ? error.message : t('offboarding.termination.saveFailed'),
        ),
    });
  };

  return (
    <StepSection step={1} title={t('offboarding.termination.title')}>
      <div className="mb-4 grid gap-3">
        <Callout
          variant="info"
          title={t('offboarding.termination.contracts.title')}
          description={t('offboarding.termination.contracts.hint')}
        />
        <PlayerContractsPanel player={offboarding.player} compact />
      </div>
      <Form {...form}>
        <form noValidate className="grid gap-4" onSubmit={form.handleSubmit(submit)}>
          <fieldset disabled={readOnly} className="grid gap-4 disabled:opacity-70">
            <div className="grid gap-4 md:grid-cols-3">
              <FormField
                control={form.control}
                name="reason"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('offboarding.termination.reason')}</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange} disabled={readOnly}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder={t('offboarding.termination.selectReason')} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {REASONS.map((reason) => (
                          <SelectItem key={reason} value={reason}>
                            {t(`offboarding.reason.${reason}`)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="noticeServedAt"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('offboarding.termination.noticeServed')}</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="effectiveDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('offboarding.termination.effectiveDate')}</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('offboarding.termination.notes')}</FormLabel>
                  <FormControl>
                    <Textarea
                      rows={3}
                      maxLength={4000}
                      placeholder={t('offboarding.termination.notesPlaceholder')}
                      {...field}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </fieldset>

          {readOnly ? null : (
            <div>
              <Button type="submit" loading={record.isPending}>
                {t('offboarding.termination.record')}
              </Button>
            </div>
          )}
        </form>
      </Form>
    </StepSection>
  );
}
